import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Paper,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { getRemoteTexts, createRemoteText, updateRemoteText, deleteRemoteText } from '../services/api';

const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
  transition: 'box-shadow 0.3s ease-in-out',
  '&:hover': {
    boxShadow: theme.shadows[6],
  },
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-child': {
    borderBottom: 'none',
  },
}));

const RemoteTextManagement = () => {
  const [remoteTexts, setRemoteTexts] = useState([]);
  const [open, setOpen] = useState(false);
  const [currentText, setCurrentText] = useState({ name: '', content: '', accessKey: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    fetchRemoteTexts();
  }, []);

  const fetchRemoteTexts = async () => {
    setLoading(true);
    try {
      const response = await getRemoteTexts();
      setRemoteTexts(response.data || []);
    } catch (error) {
      console.error('Error fetching remote texts:', error);
      setSnackbarMessage('Failed to fetch remote texts. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setIsEditing(false);
    setCurrentText({ name: '', content: '', accessKey: '' });
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSave = async () => {
    try {
      if (isEditing) {
        await updateRemoteText(currentText.id, currentText);
        setSnackbarMessage('Remote text updated successfully!');
      } else {
        await createRemoteText(currentText);
        setSnackbarMessage('New remote text created successfully!');
      }
      fetchRemoteTexts();
      handleClose();
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error saving remote text:', error);
      setSnackbarMessage('Failed to save remote text. Please try again.');
      setSnackbarOpen(true);
    }
  };

  const handleEdit = (text) => {
    setCurrentText(text);
    setIsEditing(true);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this remote text?')) {
      try {
        await deleteRemoteText(id);
        fetchRemoteTexts();
        setSnackbarMessage('Remote text deleted successfully!');
        setSnackbarOpen(true);
      } catch (error) {
        console.error('Error deleting remote text:', error);
        setSnackbarMessage('Failed to delete remote text. Please try again.');
        setSnackbarOpen(true);
      }
    }
  };

  const handleCopyUrl = (accessUrl) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(accessUrl).then(() => {
        setSnackbarMessage('URL copied to clipboard!');
        setSnackbarOpen(true);
      }).catch(() => {
        fallbackCopyTextToClipboard(accessUrl);
      });
    } else {
      fallbackCopyTextToClipboard(accessUrl);
    }
  };

  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      const successful = document.execCommand('copy');
      const msg = successful ? 'URL copied to clipboard!' : 'Unable to copy URL';
      setSnackbarMessage(msg);
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage('Failed to copy URL. Please copy manually.');
      setSnackbarOpen(true);
    }
    document.body.removeChild(textArea);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const filteredTexts = remoteTexts.filter(text =>
    text.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ maxWidth: 800, margin: 'auto', mt: 4, px: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', textAlign: 'center' }}>
        Remote Text Interface Management
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1, mr: 2 }}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpen}
        >
          Add New
        </Button>
      </Box>
      <StyledPaper elevation={3}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List>
            {filteredTexts.map((text) => (
              <StyledListItem
                key={text.id}
                secondaryAction={
                  <Box>
                    <Tooltip title="Copy URL" arrow>
                      <IconButton edge="end" aria-label="copy" onClick={() => handleCopyUrl(text.accessUrl)}>
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit" arrow>
                      <IconButton edge="end" aria-label="edit" onClick={() => handleEdit(text)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete" arrow>
                      <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(text.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              >
                <ListItemText
                  primary={text.name}
                  secondary={
                    <>
                      <Typography component="span" variant="body2" color="text.primary">
                        Created: {new Date(text.createdAt).toLocaleDateString()}
                      </Typography>
                      <br />
                      <Typography component="span" variant="body2" color="text.secondary">
                        Access URL: {text.accessUrl}
                      </Typography>
                    </>
                  }
                />
              </StyledListItem>
            ))}
          </List>
        )}
      </StyledPaper>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{isEditing ? 'Edit Remote Text' : 'Add New Remote Text'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            type="text"
            fullWidth
            value={currentText.name}
            onChange={(e) => setCurrentText({ ...currentText, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Content"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={currentText.content}
            onChange={(e) => setCurrentText({ ...currentText, content: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Access Key"
            type="text"
            fullWidth
            value={currentText.accessKey}
            onChange={(e) => setCurrentText({ ...currentText, accessKey: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">Save</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default RemoteTextManagement;